import { useGetFormalOffer } from '@api/lendingAPI/lapiHooks';
import { Spinner } from '@iwoca/orion';
import { RootState } from '@iwProvider/configureStore';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';

import styles from './Loading.module.css';
import { FormattedCurrency } from '../../../../iwIntlProvider/FormattedCurrency';
import { getDrawdownType } from '../../../drawDown/getDrawdownType';
import { TRUELAYER_REPAYMENT_TYPE } from '../../../earlyRepayment/EarlyRepaymentHelper';
import { DRAW_DOWN_MODE } from '../../../funding/ConfirmFundingView/ConfirmFunding.sagas';
import FallbackErrorModal from '../../fallbackErrorModal/FallbackErrorModal';
import { Content } from '../Content';

const mapStateToProps = (state: RootState) => ({
    ...state.processingPaymentModal
});

type PropsFromRedux = ReturnType<typeof mapStateToProps>;

const Loading = ({
    senderLogo,
    receiverLogo
}: PropsFromRedux & {
    senderLogo: string;
    receiverLogo: string;
}) => {
    return (
        <Content
            title={<LoadingTitle />}
            senderLogo={senderLogo}
            receiverLogo={receiverLogo}
            icon={<span className={styles.processingDirection} />}
        />
    );
};

const LoadingTitle = connect(mapStateToProps)(({
    mode,
    amount,
    paymentMethod,
    offerId
}: PropsFromRedux) => {
    const formattedAmount = <FormattedCurrency value={amount!} />;

    if (mode !== DRAW_DOWN_MODE) {
        if (paymentMethod === TRUELAYER_REPAYMENT_TYPE) {
            return (
                <FormattedMessage
                    id="_.processing_payment.modal.early_repayment_truelayer.loading"
                    defaultMessage="You are being redirected to Truelayer to complete the payment"
                />
            );
        } else
            return (
                <FormattedMessage
                    id="_.processing_payment.modal.early_repayment.loading"
                    defaultMessage="{amount} is being repaid by your debit card ending {paymentMethod}..."
                    values={{
                        amount: formattedAmount,
                        paymentMethod: paymentMethod
                    }}
                />
            );
    }

    return (
        <DrawdownLoading formattedAmount={formattedAmount} offerId={offerId!} />
    );
});

// eslint-disable-next-line import/no-default-export
export default connect(mapStateToProps)(Loading);

const DrawdownLoading = ({
    formattedAmount,
    offerId
}: {
    formattedAmount: React.ReactNode;
    offerId: string;
}) => {
    const { formalOffer, loadingFormalOffer, formalOfferError } =
        useGetFormalOffer(offerId);

    if (loadingFormalOffer) return <Spinner />;
    if (formalOfferError) return <FallbackErrorModal />;

    const drawdownType = getDrawdownType(formalOffer!);

    switch (drawdownType) {
        case 'repayment_reduction':
            return (
                <FormattedMessage
                    id="_.processing_payment.modal.repayment_reduction.loading"
                    defaultMessage="We're reducing your monthly repayments"
                />
            );
        case 'interest_only_extension':
            return (
                <FormattedMessage
                    id="_.processing_payment.modal.interest_only_extension.loading"
                    defaultMessage="We're extending your interest-only period..."
                />
            );
        case 'topup':
        default:
            return (
                <FormattedMessage
                    id="_.processing_payment.modal.draw_down.loading"
                    defaultMessage="We're sending {amount} to your bank account..."
                    values={{ amount: formattedAmount }}
                />
            );
    }
};
